import { __assign, __read } from "tslib";
import { isObjectType, isInterfaceType } from 'graphql';
import { getImplementingTypes, pruneSchema, filterSchema } from '@graphql-tools/utils';
import { TransformCompositeFields } from '@graphql-tools/wrap';
export function isolateComputedFieldsTransformer(subschemaConfig) {
    if (subschemaConfig.merge == null) {
        return [subschemaConfig];
    }
    var baseSchemaTypes = Object.create(null);
    var isolatedSchemaTypes = Object.create(null);
    Object.entries(subschemaConfig.merge).forEach(function (_a) {
        var _b;
        var _c = __read(_a, 2), typeName = _c[0], mergedTypeConfig = _c[1];
        baseSchemaTypes[typeName] = mergedTypeConfig;
        if (mergedTypeConfig.computedFields) {
            mergedTypeConfig.fields = (_b = mergedTypeConfig.fields) !== null && _b !== void 0 ? _b : Object.create(null);
            Object.entries(mergedTypeConfig.computedFields).forEach(function (_a) {
                var _b;
                var _c = __read(_a, 2), fieldName = _c[0], mergedFieldConfig = _c[1];
                console.warn("The \"computedFields\" setting is deprecated. Update your @graphql-tools/stitching-directives package, and/or update static merged type config to \"" + typeName + ".fields." + fieldName + " = { selectionSet: '" + mergedFieldConfig.selectionSet + "', computed: true }\"");
                mergedTypeConfig.fields[fieldName] = __assign(__assign(__assign({}, ((_b = mergedTypeConfig.fields[fieldName]) !== null && _b !== void 0 ? _b : {})), mergedFieldConfig), { computed: true });
            });
            delete mergedTypeConfig.computedFields;
        }
        if (mergedTypeConfig.fields) {
            var baseFields_1 = Object.create(null);
            var isolatedFields_1 = Object.create(null);
            Object.entries(mergedTypeConfig.fields).forEach(function (_a) {
                var _b = __read(_a, 2), fieldName = _b[0], mergedFieldConfig = _b[1];
                if (mergedFieldConfig.computed && mergedFieldConfig.selectionSet) {
                    isolatedFields_1[fieldName] = mergedFieldConfig;
                }
                else if (mergedFieldConfig.computed) {
                    throw new Error("A selectionSet is required for computed field \"" + typeName + "." + fieldName + "\"");
                }
                else {
                    baseFields_1[fieldName] = mergedFieldConfig;
                }
            });
            var isolatedFieldCount = Object.keys(isolatedFields_1).length;
            var objectType = subschemaConfig.schema.getType(typeName);
            if (isolatedFieldCount && isolatedFieldCount !== Object.keys(objectType.getFields()).length) {
                baseSchemaTypes[typeName] = __assign(__assign({}, mergedTypeConfig), { fields: baseFields_1 });
                isolatedSchemaTypes[typeName] = __assign(__assign({}, mergedTypeConfig), { fields: isolatedFields_1, canonical: undefined });
            }
        }
    });
    if (Object.keys(isolatedSchemaTypes).length) {
        return [
            filterBaseSubschema(__assign(__assign({}, subschemaConfig), { merge: baseSchemaTypes }), isolatedSchemaTypes),
            filterIsolatedSubschema(__assign(__assign({}, subschemaConfig), { merge: isolatedSchemaTypes })),
        ];
    }
    return [subschemaConfig];
}
function filterBaseSubschema(subschemaConfig, isolatedSchemaTypes) {
    var _a;
    var schema = subschemaConfig.schema;
    var typesForInterface = {};
    var filteredSchema = pruneSchema(filterSchema({
        schema: schema,
        objectFieldFilter: function (typeName, fieldName) { var _a; return !((_a = isolatedSchemaTypes[typeName]) === null || _a === void 0 ? void 0 : _a.fields[fieldName]); },
        interfaceFieldFilter: function (typeName, fieldName) {
            if (!typesForInterface[typeName]) {
                typesForInterface[typeName] = getImplementingTypes(typeName, schema);
            }
            return !typesForInterface[typeName].some(function (implementingTypeName) { var _a; return (_a = isolatedSchemaTypes[implementingTypeName]) === null || _a === void 0 ? void 0 : _a.fields[fieldName]; });
        },
    }));
    var filteredFields = {};
    Object.keys(filteredSchema.getTypeMap()).forEach(function (typeName) {
        var type = filteredSchema.getType(typeName);
        if (isObjectType(type) || isInterfaceType(type)) {
            filteredFields[typeName] = { __typename: true };
            var fieldMap = type.getFields();
            Object.keys(fieldMap).forEach(function (fieldName) {
                filteredFields[typeName][fieldName] = true;
            });
        }
    });
    var filteredSubschema = __assign(__assign({}, subschemaConfig), { merge: subschemaConfig.merge
            ? __assign({}, subschemaConfig.merge) : undefined, transforms: ((_a = subschemaConfig.transforms) !== null && _a !== void 0 ? _a : []).concat([
            new TransformCompositeFields(function (typeName, fieldName) { var _a; return (((_a = filteredFields[typeName]) === null || _a === void 0 ? void 0 : _a[fieldName]) ? undefined : null); }, function (typeName, fieldName) { var _a; return (((_a = filteredFields[typeName]) === null || _a === void 0 ? void 0 : _a[fieldName]) ? undefined : null); }),
        ]) });
    var remainingTypes = filteredSchema.getTypeMap();
    Object.keys(filteredSubschema.merge).forEach(function (mergeType) {
        if (!remainingTypes[mergeType]) {
            delete filteredSubschema.merge[mergeType];
        }
    });
    if (!Object.keys(filteredSubschema.merge).length) {
        delete filteredSubschema.merge;
    }
    return filteredSubschema;
}
function filterIsolatedSubschema(subschemaConfig) {
    var _a;
    var rootFields = {};
    Object.values(subschemaConfig.merge).forEach(function (mergedTypeConfig) {
        var _a;
        var entryPoints = (_a = mergedTypeConfig.entryPoints) !== null && _a !== void 0 ? _a : [mergedTypeConfig];
        entryPoints.forEach(function (entryPoint) {
            rootFields[entryPoint.fieldName] = true;
        });
    });
    var interfaceFields = {};
    Object.keys(subschemaConfig.merge).forEach(function (typeName) {
        subschemaConfig.schema.getType(typeName).getInterfaces().forEach(function (int) {
            Object.keys(subschemaConfig.schema.getType(int.name).getFields()).forEach(function (intFieldName) {
                if (subschemaConfig.merge[typeName].fields[intFieldName]) {
                    interfaceFields[int.name] = interfaceFields[int.name] || {};
                    interfaceFields[int.name][intFieldName] = true;
                }
            });
        });
    });
    var filteredSchema = pruneSchema(filterSchema({
        schema: subschemaConfig.schema,
        rootFieldFilter: function (operation, fieldName) { return operation === 'Query' && rootFields[fieldName] != null; },
        objectFieldFilter: function (typeName, fieldName) { var _a; return ((_a = subschemaConfig.merge[typeName]) === null || _a === void 0 ? void 0 : _a.fields[fieldName]) != null; },
        interfaceFieldFilter: function (typeName, fieldName) { var _a; return ((_a = interfaceFields[typeName]) === null || _a === void 0 ? void 0 : _a[fieldName]) != null; },
    }));
    var filteredFields = {};
    Object.keys(filteredSchema.getTypeMap()).forEach(function (typeName) {
        var type = filteredSchema.getType(typeName);
        if (isObjectType(type) || isInterfaceType(type)) {
            filteredFields[typeName] = { __typename: true };
            var fieldMap = type.getFields();
            Object.keys(fieldMap).forEach(function (fieldName) {
                filteredFields[typeName][fieldName] = true;
            });
        }
    });
    return __assign(__assign({}, subschemaConfig), { transforms: ((_a = subschemaConfig.transforms) !== null && _a !== void 0 ? _a : []).concat([
            new TransformCompositeFields(function (typeName, fieldName) { var _a; return (((_a = filteredFields[typeName]) === null || _a === void 0 ? void 0 : _a[fieldName]) ? undefined : null); }, function (typeName, fieldName) { var _a; return (((_a = filteredFields[typeName]) === null || _a === void 0 ? void 0 : _a[fieldName]) ? undefined : null); }),
        ]) });
}
//# sourceMappingURL=isolateComputedFieldsTransformer.js.map